import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { SealCheck } from "@phosphor-icons/react";

/** Approve & sign: typed full name, role and the next review date → the draft becomes the live version. */
export default function HseApproveDialog({ open, onOpenChange, doc, onApproved }) {
  const defaultReview = () => { const d = new Date(); d.setMonth(d.getMonth() + (doc?.review_months || 12)); return d.toISOString().slice(0, 10); };
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [review, setReview] = useState(defaultReview);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/hse/documents/${doc.id}/approve`, { signature_name: name, role, review_date: review });
      toast.success(`${data.reference} v${data.version} approved and signed`);
      onApproved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="hse-approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2"><SealCheck size={18} className="text-emerald-600" /> Approve &amp; sign</DialogTitle>
          <p className="text-xs text-muted-foreground">You confirm you have read this document, it reflects how {doc?.title ? "the business" : "we"} actually work, and you authorise it as the live version. Any previous approved version is superseded.</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Your full name (signature) *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Type your full name" className="h-9" data-testid="hse-approve-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Role</Label>
            <Input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Managing Director, H&S Manager" className="h-9" data-testid="hse-approve-role" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Next review date</Label>
            <DateInput value={review} onChange={e => setReview(e.target.value)} className="w-44" data-testid="hse-approve-review" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hse-approve-cancel">Cancel</Button>
          <Button onClick={approve} disabled={busy || !name.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="hse-approve-confirm">{busy ? "Signing…" : "Sign & approve"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
